import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Heart } from "lucide-react";

export default function Footer() {
  const { language } = useLanguage();

  const handleAndroidInstall = (e) => {
    e.preventDefault();
    if (window._pwaInstallPrompt) {
      window._pwaInstallPrompt.prompt();
    } else {
      window.dispatchEvent(new CustomEvent("show-pwa-instructions", { detail: { platform: "android" } }));
    }
  };

  const handleIosInstall = (e) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent("show-pwa-instructions", { detail: { platform: "ios" } }));
  };

  return (
    <footer className="bg-[#2A0A10] text-white/80 border-t border-white/10 pt-16 mt-auto">
      {/* Brand notice */}
      <div className="container-px pb-8 border-b border-white/10">
        <div className="flex items-center gap-2 mb-4">
          <Link to="/" className="inline-block hover:opacity-95 transition-opacity">
            <img src="/logo.png" alt="Hamarsathi" className="h-10 object-contain" />
          </Link>
        </div>
        <p className="text-sm text-white/70 max-w-3xl leading-relaxed">
          {language === "hi" ? (
            <>
              <Link to="/" className="text-secondary underline hover:text-white transition-colors">hamarsathi.com</Link> झारखंड के प्रमुख और सबसे भरोसेमंद विवाह पोर्टलों में से एक है। खुशहाल शादियों के सपने को साकार करते हुए, हमरसाथी विशेष रूप से भारतीय सांस्कृतिक परिवेश में सही जीवनसाथी चुनने के महत्व को समझता है। हम 100% स्क्रीनिंग, विशेष गोपनीयता विकल्प, फोटो सुरक्षा सुविधाओं और सत्यापन सुनिश्चित करके सबसे सुरक्षित और सुविधाजनक मिलान अनुभव प्रदान करने में विश्वास करते हैं। हमरसाथी एक समर्पित कस्टमर केयर टीम बनाए रखता है और सभी 24 जिलों में व्यक्तिगत मैचमेकिंग सेवाएं प्रदान करता है।
            </>
          ) : (
            <>
              <Link to="/" className="text-secondary underline hover:text-white transition-colors">hamarsathi.com</Link> is one of the leading and most trusted matrimony websites in Jharkhand.
              Making happy marriages happen, Hamarsathi understands the importance of choosing the right partner,
              especially in the Indian cultural setup. It believes in providing the most secure and convenient matchmaking
              experience by ensuring 100% screening, exclusive privacy options, photo protection features and verification.
              Hamarsathi maintains a dedicated Customer Care team and offers personalised matchmaking services across all 24 districts.
            </>
          )}
        </p>
        <p className="text-xs text-white/40 mt-3 max-w-3xl leading-relaxed">
          {language === "hi" ? t("landing.footerNotice") : "Please note: Hamarsathi is only meant for users with a bonafide intent to enter into a matrimonial alliance and is not meant for users interested in dating only. The platform should not be used to post any obscene material."}
        </p>
      </div>

      {/* Links + App */}
      <div className="container-px py-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Explore */}
          <div>
            <div className="text-xs uppercase tracking-widest text-white/40 font-semibold mb-4">{language === "hi" ? "अन्वेषण करें" : "Explore"}</div>
            <ul className="space-y-2.5 text-sm">
              {[
                [language === "hi" ? "मुख्य पृष्ठ" : "Home", "/"],
                [language === "hi" ? "प्रोफ़ाइल खोजें" : "Browse Profiles", "/app/browse"],
                [language === "hi" ? "सफलता की कहानियाँ" : "Success Stories", "/success-stories"],
                [language === "hi" ? "साइटमैप" : "Sitemap", "/sitemap"]
              ].map(([label, href]) => (
                <li key={label}><Link to={href} className="hover:text-secondary transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
          {/* Services */}
          <div>
            <div className="text-xs uppercase tracking-widest text-white/40 font-semibold mb-4">{language === "hi" ? "सेवाएं" : "Services"}</div>
            <ul className="space-y-2.5 text-sm">
              {[
                [language === "hi" ? "सदस्यता योजनाएं" : "Membership Plans", "/app/premium"],
                [language === "hi" ? "कुंडली मिलान" : "Kundli Match", "/app/kundli"],
                [language === "hi" ? "एक्सक्लूसिव" : "Exclusive", "/app/premium"],
                [language === "hi" ? "सत्यापित बैज" : "Verified Badge", "/app/verify"]
              ].map(([label, href]) => (
                <li key={label}><Link to={href} className="hover:text-secondary transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
          {/* Help */}
          <div>
            <div className="text-xs uppercase tracking-widest text-white/40 font-semibold mb-4">{language === "hi" ? "सहायता" : "Help"}</div>
            <ul className="space-y-2.5 text-sm">
              {[
                [language === "hi" ? "संपर्क करें" : "Contact Us", "/contact"],
                [language === "hi" ? "पूछे जाने वाले प्रश्न" : "FAQs", "/faqs"],
                [language === "hi" ? "सुरक्षा युक्तियाँ" : "Safety Tips", "/safety-tips"],
                [language === "hi" ? "दुरुपयोग की रिपोर्ट" : "Report Abuse", "/contact?subject=report"]
              ].map(([label, href]) => (
                <li key={label}><Link to={href} className="hover:text-secondary transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
          {/* Legal */}
          <div>
            <div className="text-xs uppercase tracking-widest text-white/40 font-semibold mb-4">{language === "hi" ? "कानूनी" : "Legal"}</div>
            <ul className="space-y-2.5 text-sm">
              {[
                [language === "hi" ? "हमारे बारे में" : "About Us", "/about"],
                [language === "hi" ? "उपयोग की शर्तें" : "Terms of Use", "/terms"],
                [language === "hi" ? "गोपनीयता नीति" : "Privacy Policy", "/privacy-policy"],
                [language === "hi" ? "कुकी नीति" : "Cookie Policy", "/cookie-policy"],
                [language === "hi" ? "शिकायतें" : "Grievances", "/grievances"]
              ].map(([label, href]) => (
                <li key={label}><Link to={href} className="hover:text-secondary transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
          {/* Features */}
          <div>
            <div className="text-xs uppercase tracking-widest text-white/40 font-semibold mb-4">{language === "hi" ? "विशेषताएं" : "Features"}</div>
            <ul className="space-y-2.5 text-sm">
              {[
                [language === "hi" ? "सत्यापित प्रोफाइल" : "Verified Profiles", "/app/browse?verified=true"],
                [language === "hi" ? "गोपनीयता नियंत्रण" : "Privacy Controls", "/app/privacy"],
                [language === "hi" ? "कुंडली मिलान" : "Kundli Matching", "/app/kundli"],
                [language === "hi" ? "चैट और कनेक्ट" : "Chat & Connect", "/app/chat"]
              ].map(([label, href]) => (
                <li key={label}><Link to={href} className="hover:text-secondary transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
          {/* App + Social */}
          <div>
            <div className="text-xs uppercase tracking-widest text-white/40 font-semibold mb-4">{language === "hi" ? "ऐप प्राप्त करें" : "Get the App"}</div>
            <button onClick={handleAndroidInstall}
              className="block mb-2 hover:opacity-80 transition focus:outline-none text-left">
              <img src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Google Play" className="h-9" />
            </button>
            <button onClick={handleIosInstall}
              className="block hover:opacity-80 transition focus:outline-none text-left">
              <img src="https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg"
                alt="App Store" className="h-9" />
            </button>
            {/* Social */}
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-white/40 font-semibold mb-3">{language === "hi" ? "हमसे जुड़ें" : "Connect with us"}</div>
              <div className="flex gap-2.5 flex-wrap">
                {[
                  { label: "Facebook",  bg: "bg-blue-600", icon: "f", url: "https://www.facebook.com/joidimilan" },
                  { label: "Instagram", bg: "bg-gradient-to-br from-pink-500 to-purple-600", icon: "📷", url: "https://www.instagram.com/hamar_sathi" },
                ].map((s) => (
                  <a key={s.label} href={s.url} target="_blank" rel="noreferrer" aria-label={s.label}
                    className={`w-8 h-8 rounded-full ${s.bg} flex items-center justify-center text-white text-xs font-bold hover:scale-110 transition-transform`}>
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container-px py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <span>© {new Date().getFullYear()} {language === "hi" ? "हमरसाथी। सर्वाधिकार सुरक्षित।" : "Hamarsathi. All rights reserved."}</span>
          <span className="flex items-center gap-1">
            {language === "hi" ? (
              <>हजारीबाग, झारखंड में <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500 mx-0.5 inline-block" /> के साथ बनाया गया | <a href="https://www.adpixa.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors">By Adpixa</a></>
            ) : (
              <>Made with <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500 mx-0.5 inline-block" /> In Hazaribagh, Jharkhand | <a href="https://www.adpixa.com" target="_blank" rel="noopener noreferrer" className="underline hover:text-white transition-colors">By Adpixa</a></>
            )}
          </span>
        </div>
      </div>
    </footer>
  );
}
